/**
 * Resurs bo'yicha API qatlami. Javoblar contracts zod schema'lari bilan parse
 * qilinadi — backend shartnomani buzsa, xato shu yerda chiqadi.
 */
import {
  aiTestJobSchema,
  aiTestsPullResponseSchema,
  activeAiTestsResponseSchema,
  bookOrganizationsResponseSchema,
  organizationDetailSchema,
  organizationsResponseSchema,
  bookAiTestsResponseSchema,
  bookDifficultyMixResponseSchema,
  globalDifficultyMixResponseSchema,
  adminBookListResponseSchema,
  adminExamListResponseSchema,
  bookSchema,
  booksCountResponseSchema,
  changeExamStatusResponseSchema,
  deleteExamResponseSchema,
  deleteScheduleEventResponseSchema,
  detailedScheduleEventsResponseSchema,
  employeeBookListResponseSchema,
  employeeCountResponseSchema,
  employeeExamListResponseSchema,
  examResponseSchema,
  examResultResponseSchema,
  fileUploadResponseSchema,
  genreListResponseSchema,
  importQuestionsResponseSchema,
  leaderboardResponseSchema,
  logListResponseSchema,
  logSchema,
  meResponseSchema,
  questionListResponseSchema,
  questionSchema,
  readStatsResponseSchema,
  scheduledListResponseSchema,
  spiritualIndexResponseSchema,
  staffStatisticsResponseSchema,
  subjectListResponseSchema,
  toggleFavoriteResponseSchema,
  updateScheduleEventResponseSchema,
  type AdminBookListQuery,
  type AdminExamListQuery,
  type CreateBookInput,
  type CreateLogInput,
  type CreateQuestionInput,
  type DifficultyMix,
  type EmployeeBookListQuery,
  type EmployeeExamListQuery,
  type FinishExamInput,
  type LeaderboardQuery,
  type ListLogsQuery,
  type ListQuestionsQuery,
  type PaginationQuery,
  type RateBookInput,
  type ReadStatsQuery,
  type ScheduleEventsQuery,
  type ScheduleExamInput,
  type SpiritualIndexQuery,
  type UpdateBookInput,
  type UpdateQuestionInput,
  type UpdateScheduleEventInput,
  type UploadFileType,
} from '@kitobxonlik/contracts';
import { api, cleanParams, parseContentRange } from './client';

type Q = Record<string, unknown>;

export const meApi = {
  get: async () => meResponseSchema.parse((await api.get('/employee/v1/me')).data),
};

export const dictionariesApi = {
  genres: async () => genreListResponseSchema.parse((await api.get('/admin/v1/genres')).data).data,
  subjects: async () =>
    subjectListResponseSchema.parse((await api.get('/admin/v1/subjects')).data).data,
  createGenre: (name: string) => api.post('/admin/v1/genres', { name }),
  updateGenre: (id: number, name: string) => api.put(`/admin/v1/genres/${id}`, { name }),
  deleteGenre: (id: number) => api.delete(`/admin/v1/genres/${id}`),
  createSubject: (name: string) => api.post('/admin/v1/subjects', { name }),
  updateSubject: (id: number, name: string) => api.put(`/admin/v1/subjects/${id}`, { name }),
  deleteSubject: (id: number) => api.delete(`/admin/v1/subjects/${id}`),
};

export const booksApi = {
  /** Xodim katalogi (o'z + host org'lar) */
  list: async (params: EmployeeBookListQuery) =>
    employeeBookListResponseSchema.parse(
      (await api.get('/employee/v1/books', { params: cleanParams(params as Q) })).data,
    ),
  favorites: async (params: EmployeeBookListQuery) =>
    employeeBookListResponseSchema.parse(
      (await api.get('/employee/v1/books/favorite', { params: cleanParams(params as Q) })).data,
    ),
  enrolled: async (params: EmployeeBookListQuery) =>
    employeeBookListResponseSchema.parse(
      (await api.get('/employee/v1/books/enrolled', { params: cleanParams(params as Q) })).data,
    ),
  toggleFavorite: async (id: number) =>
    toggleFavoriteResponseSchema.parse(
      (await api.patch(`/employee/v1/books/${id}/toggle-favorite`)).data,
    ),
  rate: (id: number, input: RateBookInput) => api.post(`/employee/v1/books/${id}/rate`, input),
  schedule: (id: number, input: ScheduleExamInput) =>
    api.post(`/employee/v1/books/${id}/schedule`, input),
  mySpiritualIndex: async (params: SpiritualIndexQuery = {}) =>
    spiritualIndexResponseSchema.parse(
      (await api.get('/employee/v1/books/spirituality-index', { params: cleanParams(params as Q) }))
        .data,
    ),

  /** Admin */
  adminList: async (params: AdminBookListQuery) =>
    adminBookListResponseSchema.parse(
      (await api.get('/admin/v1/books', { params: cleanParams(params as Q) })).data,
    ),
  get: async (id: number) => bookSchema.parse((await api.get(`/admin/v1/books/${id}`)).data),
  create: async (input: CreateBookInput) =>
    bookSchema.parse((await api.post('/admin/v1/books', input)).data),
  update: async (id: number, input: UpdateBookInput) =>
    bookSchema.parse((await api.put(`/admin/v1/books/${id}`, input)).data),
  remove: (id: number) => api.delete(`/admin/v1/books/${id}`),
  count: async (orgId: number) =>
    booksCountResponseSchema.parse(
      (await api.get('/admin/v1/books/count', { params: { org_id: orgId } })).data,
    ),
};

export const analyticsApi = {
  readStats: async (params: ReadStatsQuery) =>
    readStatsResponseSchema.parse(
      (await api.get('/admin/v1/books/read-stats', { params: cleanParams(params as Q) })).data,
    ),
  leaderboard: async (params: LeaderboardQuery) =>
    leaderboardResponseSchema.parse(
      (await api.get('/admin/v1/books/leaderboard', { params: cleanParams(params as Q) })).data,
    ),
  spiritualIndex: async (params: SpiritualIndexQuery = {}) =>
    spiritualIndexResponseSchema.parse(
      (await api.get('/admin/v1/books/spirituality-index', { params: cleanParams(params as Q) }))
        .data,
    ),
};

/** Kadrlar kesimi — HR `state` servisiga apps/api orqali proxy */
export const staffStatsApi = {
  staff: async (all = false) =>
    staffStatisticsResponseSchema.parse(
      (await api.get('/admin/v1/statistics/staff', { params: { all: String(all) } })).data,
    ),
  /** Xodimlar soni: HR'dagi, qo'lda kiritilgan va hisobda ishlatiladigani */
  employeeCount: async (orgId: number) =>
    employeeCountResponseSchema.parse(
      (await api.get('/admin/v1/statistics/employee-count', { params: { org_id: orgId } })).data,
    ),
  /** Qo'lda kiritish — HR'dagi sondan ustun */
  setEmployeeCount: async (orgId: number, count: number) =>
    employeeCountResponseSchema.parse(
      (
        await api.put(
          '/admin/v1/statistics/employee-count',
          { count },
          { params: { org_id: orgId } },
        )
      ).data,
    ),
  /** Qo'lda kiritilganini o'chirish — son yana HR'dan */
  clearEmployeeCount: async (orgId: number) =>
    employeeCountResponseSchema.parse(
      (await api.delete('/admin/v1/statistics/employee-count', { params: { org_id: orgId } })).data,
    ),
};

export const filesApi = {
  upload: async (file: File, type: UploadFileType, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await api.post('/admin/v1/files/upload', form, {
      params: { type },
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return fileUploadResponseSchema.parse(res.data);
  },
  /** Imtihon videosi: upload_id = exam id */
  uploadChunk: async (
    uploadId: number,
    chunkIndex: number,
    isLast: boolean,
    chunk: Blob | null,
  ) => {
    const params = { upload_id: uploadId, chunk_index: chunkIndex, is_last: String(isLast) };
    if (!chunk) {
      await api.post('/employee/v1/files/upload-chunk', undefined, { params });
      return;
    }
    const form = new FormData();
    form.append('chunk', chunk, `chunk-${chunkIndex}.webm`);
    await api.post('/employee/v1/files/upload-chunk', form, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /**
   * O'qish oynasi: faylning `[start, end]` baytlari (HTTP Range, ikkala chegara
   * kiradi) va faylning to'liq hajmi. `<img>` kabi to'g'ridan URL emas, axios
   * orqali — uzoq o'qishda access token eskirsa interceptor uni yangilaydi.
   * Server Range'ni bermasa (200) — butun fayl keladi.
   */
  readRange: async (id: string, start: number, end: number) => {
    const res = await api.get<ArrayBuffer>(`/v1/files/${id}/preview`, {
      responseType: 'arraybuffer',
      headers: { Range: `bytes=${start}-${end}`, Accept: '*/*' },
    });
    const data = new Uint8Array(res.data);
    const range = res.status === 206 ? parseContentRange(res.headers['content-range']) : null;
    return range
      ? { data, start: range.start, total: range.total }
      : { data, start: 0, total: data.length };
  },
};

export const questionsApi = {
  list: async (params: ListQuestionsQuery) =>
    questionListResponseSchema.parse(
      (await api.get('/admin/v1/questions', { params: cleanParams(params as Q) })).data,
    ),
  get: async (id: number) =>
    questionSchema.parse((await api.get(`/admin/v1/questions/${id}`)).data),
  create: async (input: CreateQuestionInput) =>
    questionSchema.parse((await api.post('/admin/v1/questions', input)).data),
  update: async (id: number, input: UpdateQuestionInput) =>
    questionSchema.parse((await api.put(`/admin/v1/questions/${id}`, input)).data),
  remove: (id: number) => api.delete(`/admin/v1/questions/${id}`),
  import: async (fileId: string, bookId: number) =>
    importQuestionsResponseSchema.parse(
      (await api.post('/admin/v1/questions/import', { file_id: fileId, book_id: bookId })).data,
    ),
};

/** Imtihondagi savollar qiyinlik ulushi: umumiy va kitobga alohida */
export const difficultyMixApi = {
  getGlobal: async () =>
    globalDifficultyMixResponseSchema.parse((await api.get('/admin/v1/difficulty-mix')).data),
  setGlobal: async (mix: DifficultyMix) =>
    globalDifficultyMixResponseSchema.parse((await api.put('/admin/v1/difficulty-mix', mix)).data),
  getForBook: async (bookId: number) =>
    bookDifficultyMixResponseSchema.parse(
      (await api.get(`/admin/v1/books/${bookId}/difficulty-mix`)).data,
    ),
  setForBook: async (bookId: number, mix: DifficultyMix) =>
    bookDifficultyMixResponseSchema.parse(
      (await api.put(`/admin/v1/books/${bookId}/difficulty-mix`, mix)).data,
    ),
  resetForBook: async (bookId: number) =>
    bookDifficultyMixResponseSchema.parse(
      (await api.delete(`/admin/v1/books/${bookId}/difficulty-mix`)).data,
    ),
};

/** Kitobni AI'ga test tayyorlash uchun yuborish, jarayon holati va tayyor testni qayta olish */
export const aiTestsApi = {
  active: async () =>
    activeAiTestsResponseSchema.parse((await api.get('/admin/v1/books/ai-tests/active')).data),
  latest: async (bookId: number) =>
    bookAiTestsResponseSchema.parse((await api.get(`/admin/v1/books/${bookId}/ai-tests`)).data),
  send: async (bookId: number) =>
    aiTestJobSchema.parse((await api.post(`/admin/v1/books/${bookId}/ai-tests`)).data),
  pull: async (bookId: number) =>
    aiTestsPullResponseSchema.parse(
      (await api.post(`/admin/v1/books/${bookId}/ai-tests/pull`)).data,
    ),
};

export const examsApi = {
  start: async (bookId: number) =>
    examResponseSchema.parse(
      (await api.post('/employee/v1/exams', undefined, { params: { book_id: bookId } })).data,
    ),
  get: async (id: number) =>
    examResponseSchema.parse((await api.get(`/employee/v1/exams/${id}`)).data),
  finish: async (id: number, input: FinishExamInput) =>
    examResponseSchema.parse((await api.put(`/employee/v1/exams/${id}`, input)).data),
  result: async (id: number) =>
    examResultResponseSchema.parse((await api.get(`/employee/v1/exams/${id}/result`)).data),
  myList: async (params: EmployeeExamListQuery) =>
    employeeExamListResponseSchema.parse(
      (await api.get('/employee/v1/exams', { params: cleanParams(params as Q) })).data,
    ),
  myEvents: async (params: ScheduleEventsQuery) =>
    detailedScheduleEventsResponseSchema.parse(
      (
        await api.get('/employee/v1/exams/detailed-schedule-events', {
          params: cleanParams(params as Q),
        })
      ).data,
    ).list,
  updateEvent: async (id: number, input: UpdateScheduleEventInput) =>
    updateScheduleEventResponseSchema.parse(
      (await api.put(`/employee/v1/exams/schedule-events/${id}`, input)).data,
    ).event,
  deleteEvent: async (id: number) =>
    deleteScheduleEventResponseSchema.parse(
      (await api.delete(`/employee/v1/exams/schedule-events/${id}`)).data,
    ),

  /** Admin */
  adminList: async (params: AdminExamListQuery) =>
    adminExamListResponseSchema.parse(
      (await api.get('/admin/v1/exams', { params: cleanParams(params as Q) })).data,
    ),
  adminEvents: async (params: ScheduleEventsQuery) =>
    detailedScheduleEventsResponseSchema.parse(
      (
        await api.get('/admin/v1/exams/detailed-schedule-events', {
          params: cleanParams(params as Q),
        })
      ).data,
    ).list,
  scheduled: async (params: PaginationQuery & { org_id?: number; book_id?: number }) =>
    scheduledListResponseSchema.parse(
      (await api.get('/admin/v1/exams/scheduled', { params: cleanParams(params as Q) })).data,
    ),
  scheduledCount: async (orgId: number) =>
    booksCountResponseSchema.parse(
      (await api.get('/admin/v1/exams/scheduled/count', { params: { org_id: orgId } })).data,
    ),
  changeStatus: async (id: number, status: 'failed') =>
    changeExamStatusResponseSchema.parse(
      (await api.patch('/admin/v1/exams/status', { id, status })).data,
    ),
  /** O'chirish (soft delete, bazada qoladi) — faqat DEVELOPER (`exam:delete`) */
  remove: async (id: number) =>
    deleteExamResponseSchema.parse((await api.delete(`/admin/v1/exams/${id}`)).data),
};

export const logsApi = {
  create: async (input: CreateLogInput) =>
    logSchema.parse((await api.post('/admin/v1/logs', input)).data),
  list: async (params: ListLogsQuery) =>
    logListResponseSchema.parse(
      (await api.get('/admin/v1/logs', { params: cleanParams(params as Q) })).data,
    ),
};

/** Tashkilotlar va kitobni ularga biriktirish — faqat SUPER_ADMIN / DEVELOPER (2026-10-30) */
export const organizationsApi = {
  list: async () =>
    organizationsResponseSchema.parse((await api.get('/admin/v1/organizations')).data).data,
  get: async (id: number) =>
    organizationDetailSchema.parse((await api.get(`/admin/v1/organizations/${id}`)).data),
  forBook: async (bookId: number) =>
    bookOrganizationsResponseSchema.parse(
      (await api.get(`/admin/v1/books/${bookId}/organizations`)).data,
    ).data,
  setForBook: (bookId: number, orgIds: number[]) =>
    api.put(`/admin/v1/books/${bookId}/organizations`, { org_ids: orgIds }),
};
